import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { SeverityBadge, SEVERITY } from "../components/SeverityBadge";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Bug, CaretDown, CaretRight, ShieldCheck, FileMagnifyingGlass, Wrench } from "@phosphor-icons/react";

const FILTERS = ["all", "critical", "high", "medium", "low", "info"];

export default function Findings() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ key: "severity", dir: "asc" });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/findings").then((r) => setItems(r.data));
  }, []);

  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === "all" ? items.length : items.filter((i) => i.severity === f).length;
    return acc;
  }, {});

  let rows = filter === "all" ? items : items.filter((i) => i.severity === filter);
  const order = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  rows = [...rows].sort((a, b) => {
    let av, bv;
    if (sort.key === "severity") { av = order[a.severity]; bv = order[b.severity]; }
    else if (sort.key === "cvss") { av = a.cvss; bv = b.cvss; }
    else { av = (a[sort.key] || "").toString(); bv = (b[sort.key] || "").toString(); }
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const Th = ({ label, k }) => (
    <TableHead onClick={() => toggleSort(k)} data-testid={`sort-${k}`}
      className="overline text-slate-500 cursor-pointer select-none hover:text-slate-300">
      {label}{sort.key === k ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
    </TableHead>
  );

  return (
    <div className="fade-up">
      <PageHeader
        overline="Vulnerabilities"
        title="Findings"
        subtitle="CVE-backed findings with CVSS severity. Click a row to expand evidence and remediation."
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium capitalize transition-colors duration-150 ${
              filter === f ? "border-blue-500 bg-blue-500/10 text-blue-300" : "border-[#1e293b] text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}>
            {f !== "all" && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: SEVERITY[f].color }} />}
            {f} <span className="text-slate-500">({counts[f]})</span>
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={ShieldCheck} title="No findings match" hint="Run a vulnerability or exploitation scan to populate findings." />
      ) : (
        <Card className="bg-[#0f172a] border-[#1e293b] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-[#1e293b] hover:bg-transparent">
                <TableHead className="w-8"></TableHead>
                <Th label="Severity" k="severity" />
                <Th label="Finding" k="title" />
                <Th label="CVE" k="cve" />
                <Th label="CVSS" k="cvss" />
                <Th label="Host" k="host" />
                <Th label="Service" k="service" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((f) => (
                <React.Fragment key={f.id}>
                  <TableRow data-testid={`finding-row-${f.id}`}
                    onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                    className="border-[#1e293b] hover:bg-slate-800/40 cursor-pointer">
                    <TableCell className="text-slate-500">
                      {expanded === f.id ? <CaretDown size={16} /> : <CaretRight size={16} />}
                    </TableCell>
                    <TableCell><SeverityBadge severity={f.severity} /></TableCell>
                    <TableCell className="text-white font-medium max-w-xs truncate">{f.title}</TableCell>
                    <TableCell className="font-mono text-blue-300 text-sm">{f.cve}</TableCell>
                    <TableCell className="font-mono text-white">{f.cvss}</TableCell>
                    <TableCell className="font-mono text-slate-300 text-sm">{f.host}:{f.port}</TableCell>
                    <TableCell className="text-slate-400 text-sm">{f.service}</TableCell>
                  </TableRow>
                  {expanded === f.id && (
                    <TableRow className="border-[#1e293b] bg-[#0b1220]" data-testid={`finding-detail-${f.id}`}>
                      <TableCell colSpan={7} className="py-4">
                        <div className="grid md:grid-cols-3 gap-5 px-2">
                          <Detail icon={FileMagnifyingGlass} title="Description" body={f.description} />
                          <Detail icon={Bug} title="Evidence / Proof" body={f.evidence} mono />
                          <Detail icon={Wrench} title="Remediation" body={f.remediation} accent />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

function Detail({ icon: Icon, title, body, mono, accent }) {
  return (
    <div>
      <div className={`flex items-center gap-1.5 overline mb-2 ${accent ? "text-blue-400" : "text-slate-500"}`}>
        <Icon size={14} weight="bold" /> {title}
      </div>
      <p className={`text-sm leading-relaxed ${mono ? "font-mono text-xs bg-[#020817] rounded p-2 border border-[#1e293b] text-emerald-300" : "text-slate-300"}`}>
        {body}
      </p>
    </div>
  );
}
